import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useI18n } from '@/lib/i18n';
import { List, X } from '@phosphor-icons/react';

const LOGO = '/images/logo.jpg';

const navItems = [
  { id: 'about', key: 'about' },
  { id: 'team', key: 'team' },
  { id: 'services', key: 'services' },
  { id: 'whyus', key: 'whyus' },
  { id: 'growth', key: 'growth' },
  { id: 'contact', key: 'contact' },
];

export default function Header({ onApply }) {
  const { t, lang, setLang } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const onLanding = location.pathname === '/';

  const goHome = (e) => {
    e.preventDefault();
    setOpen(false);
    if (onLanding) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    }
  };

  const scrollTo = (id) => {
    if (!onLanding) {
      window.location.href = `/#${id}`;
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setOpen(false);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-[#0A1128]/95 backdrop-blur-xl border-b border-white/5' : 'bg-[#0A1128]/40 backdrop-blur-md'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 flex items-center justify-between h-20">
        <Link to="/" onClick={goHome} data-testid="logo-link" className="flex items-center gap-3 group cursor-pointer">
          <img
            src={LOGO}
            alt="De Melo Recruiting"
            data-testid="logo-img"
            className="h-12 w-12 object-cover border border-white/10"
          />
          <div className="leading-tight hidden sm:block">
            <div className="text-white text-[14px] font-medium tracking-tight">De Melo Recruiting</div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Freiburg · DE</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {navItems.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-${n.id}`}
              onClick={() => scrollTo(n.id)}
              className="text-[13px] tracking-wide text-white/80 hover:text-[#C5A059] transition-colors"
            >
              {t.nav[n.key]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div className="hidden sm:flex items-center gap-1 text-[12px]">
            <button data-testid="lang-de" onClick={() => setLang('de')} className={`px-2 py-1 ${lang === 'de' ? 'text-[#C5A059]' : 'text-white/60 hover:text-white'}`}>DE</button>
            <span className="text-white/30">/</span>
            <button data-testid="lang-en" onClick={() => setLang('en')} className={`px-2 py-1 ${lang === 'en' ? 'text-[#C5A059]' : 'text-white/60 hover:text-white'}`}>EN</button>
          </div>
          <button
            data-testid="header-apply-btn"
            onClick={onApply}
            className="hidden md:inline-flex items-center bg-[#C5A059] hover:bg-[#B38F4D] text-[#0A1128] px-5 py-2.5 text-[11px] font-semibold tracking-[0.18em] uppercase transition-colors"
          >
            {t.nav.apply}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label="menu"
          >
            {open ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#0A1128] border-t border-white/5">
          <div className="px-6 py-6 flex flex-col gap-4">
            {navItems.map((n) => (
              <button
                key={n.id}
                onClick={() => scrollTo(n.id)}
                className="text-left text-white/80 hover:text-[#C5A059] text-sm tracking-wide"
                data-testid={`mobile-nav-${n.id}`}
              >
                {t.nav[n.key]}
              </button>
            ))}
            <div className="flex items-center gap-2 pt-2 text-xs">
              <button onClick={() => setLang('de')} className={lang === 'de' ? 'text-[#C5A059]' : 'text-white/60'}>DE</button>
              <span className="text-white/30">/</span>
              <button onClick={() => setLang('en')} className={lang === 'en' ? 'text-[#C5A059]' : 'text-white/60'}>EN</button>
            </div>
            <button
              onClick={onApply}
              data-testid="mobile-apply-btn"
              className="mt-2 bg-[#C5A059] text-[#0A1128] px-5 py-3 text-[11px] font-semibold tracking-[0.18em] uppercase"
            >
              {t.nav.apply}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
